import { motion, AnimatePresence } from 'framer-motion';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAboutMobileOpen, setIsAboutMobileOpen] = useState(false);

  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 }
  };

  const mobileMenuVariants = {
    closed: { opacity: 0, height: 0 },
    open: { opacity: 1, height: "auto" }
  };

  // Professional dropdown animations
  const dropdownVariants = {
    hidden: { 
      opacity: 0, 
      y: -15, 
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        duration: 0.3,
        staggerChildren: 0.06,
        delayChildren: 0.1
      }
    }
  };

  const dropdownItemVariants = {
    hidden: { 
      opacity: 0, 
      x: -12,
      y: -3
    },
    visible: { 
      opacity: 1, 
      x: 0,
      y: 0,
      transition: { 
        duration: 0.25
      }
    }
  };

  const [isAboutOpen, setIsAboutOpen] = useState(false);

  const aboutCloseTimerRef = useRef<number | null>(null);
  const handleAboutOpen = () => {
    if (aboutCloseTimerRef.current) {
      clearTimeout(aboutCloseTimerRef.current);
      aboutCloseTimerRef.current = null;
    }
    setIsAboutOpen(true);
  };
  const handleAboutCloseWithDelay = () => {
    if (aboutCloseTimerRef.current) {
      clearTimeout(aboutCloseTimerRef.current);
    }
    aboutCloseTimerRef.current = window.setTimeout(() => setIsAboutOpen(false), 150);
  };

  return (
    <motion.nav 
      className="bg-white shadow-sm py-4 sticky top-10 md:top-[72px] z-40"
      variants={navVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between md:justify-center">
          {/* Mobile Menu Button */}
          <motion.button
            className="md:hidden flex items-center p-2 rounded-md text-dblue hover:bg-gray-100"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </motion.button>

          {/* Left Navigation Links - Desktop Only */}
          <motion.div 
            className="hidden md:flex items-center gap-6 mr-8"
            variants={itemVariants}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link 
                to="/home" 
                className="text-dblue hover:text-blue-600 font-medium uppercase text-sm transition-colors relative"
              >
                <span>Home</span>
                <motion.div
                  className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600"
                  whileHover={{ width: "100%" }}
                  transition={{ duration: 0.2 }}
                />
              </Link>
            </motion.div>
            <div className="relative">
              <motion.div 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }}
                className="relative"
              >
                <motion.button 
                  className="text-dblue hover:text-blue-600 font-medium uppercase text-sm transition-colors relative group"
                  onMouseEnter={handleAboutOpen}
                  onMouseLeave={handleAboutCloseWithDelay}
                  whileHover={{ y: -1 }}
                  transition={{ duration: 0.2 }}
                >
                  <span className="relative z-10">About</span>
                  <motion.div
                    className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600"
                    animate={{ width: isAboutOpen ? "100%" : "0%" }}
                    transition={{ duration: 0.2 }}
                  />
                  <motion.div
                    className="absolute inset-0 bg-blue-50 rounded-md opacity-0 group-hover:opacity-100 -z-10"
                    transition={{ duration: 0.2 }}
                  />
                </motion.button>
              </motion.div>
              <AnimatePresence>
              {isAboutOpen && (
                  <motion.div 
                  className="absolute left-0 top-full pt-2 z-50"
                  onMouseEnter={handleAboutOpen}
                  onMouseLeave={handleAboutCloseWithDelay}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={dropdownVariants}
                  >
                  <motion.div 
                    className="w-56 bg-white border border-gray-200 rounded-lg shadow-xl py-2 backdrop-blur-sm"
                    style={{ boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)" }}
                  >
                    <motion.div variants={dropdownItemVariants}>
                      <Link 
                        to="/about/educators" 
                        className="block px-4 py-3 text-dblue hover:bg-blue-50 hover:text-blue-700 text-sm font-medium transition-all duration-200 rounded-md mx-2 relative overflow-hidden group" 
                        onClick={() => setIsAboutOpen(false)}
                      >
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          initial={{ x: "-100%" }}
                          whileHover={{ x: "0%" }}
                          transition={{ duration: 0.3 }}
                        />
                        <span className="relative z-10">Educators</span>
                      </Link>
                    </motion.div>
                    <motion.div variants={dropdownItemVariants}>
                      <Link 
                        to="/about/admin-staff" 
                        className="block px-4 py-3 text-dblue hover:bg-blue-50 hover:text-blue-700 text-sm font-medium transition-all duration-200 rounded-md mx-2 relative overflow-hidden group" 
                        onClick={() => setIsAboutOpen(false)}
                      >
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          initial={{ x: "-100%" }}
                          whileHover={{ x: "0%" }}
                          transition={{ duration: 0.3 }}
                        />
                        <span className="relative z-10">Admin Staff</span>
                      </Link>
                    </motion.div>
                    <motion.div variants={dropdownItemVariants}>
                      <Link 
                        to="/about/classrooms" 
                        className="block px-4 py-3 text-dblue hover:bg-blue-50 hover:text-blue-700 text-sm font-medium transition-all duration-200 rounded-md mx-2 relative overflow-hidden group" 
                        onClick={() => setIsAboutOpen(false)}
                      >
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          initial={{ x: "-100%" }}
                          whileHover={{ x: "0%" }}
                          transition={{ duration: 0.3 }}
                        />
                        <span className="relative z-10">Classrooms</span>
                      </Link>
                    </motion.div>
                    <motion.div variants={dropdownItemVariants}>
                      <Link 
                        to="/about/environment" 
                        className="block px-4 py-3 text-dblue hover:bg-blue-50 hover:text-blue-700 text-sm font-medium transition-all duration-200 rounded-md mx-2 relative overflow-hidden group" 
                        onClick={() => setIsAboutOpen(false)}
                      >
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          initial={{ x: "-100%" }}
                          whileHover={{ x: "0%" }}
                          transition={{ duration: 0.3 }}
                        />
                        <span className="relative z-10">Environment</span>
                      </Link>
                    </motion.div>
                    <motion.div variants={dropdownItemVariants}>
                      <Link 
                        to="/about/home-made-meals" 
                        className="block px-4 py-3 text-dblue hover:bg-blue-50 hover:text-blue-700 text-sm font-medium transition-all duration-200 rounded-md mx-2 relative overflow-hidden group" 
                        onClick={() => setIsAboutOpen(false)}
                      >
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          initial={{ x: "-100%" }}
                          whileHover={{ x: "0%" }}
                          transition={{ duration: 0.3 }}
                        />
                        <span className="relative z-10">Home-made Meals</span>
                      </Link>
                    </motion.div>
                    <motion.div variants={dropdownItemVariants}>
                      <Link 
                        to="/about/teaching-methods" 
                        className="block px-4 py-3 text-dblue hover:bg-blue-50 hover:text-blue-700 text-sm font-medium transition-all duration-200 rounded-md mx-2 relative overflow-hidden group" 
                        onClick={() => setIsAboutOpen(false)}
                      >
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          initial={{ x: "-100%" }}
                          whileHover={{ x: "0%" }}
                          transition={{ duration: 0.3 }}
                        />
                        <span className="relative z-10">Teaching Methods</span>
                      </Link>
                    </motion.div>
                  </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link 
                to="/enrollment" 
                className="text-dblue hover:text-blue-600 font-medium uppercase text-sm transition-colors relative"
              >
                <span>Enrollment</span>
                <motion.div
                  className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600"
                  whileHover={{ width: "100%" }}
                  transition={{ duration: 0.2 }}
                />
              </Link>
            </motion.div>
          </motion.div>

          {/* Centered Logo */}
          <motion.div 
            className="flex-shrink-0"
            variants={itemVariants}
            whileHover={{ 
              scale: 1.05,
              rotate: [0, -2, 2, 0],
              transition: { duration: 0.3 }
            }}
          >
            <Link to="/home" aria-label="Go to home">
              <img src="/images/harbour_logo-300dpi-1536x754.jpg" alt="Harbourside Children's Centre" className="h-12 md:h-16" />
            </Link>
          </motion.div>

          {/* Right Navigation Links - Desktop Only */}
          <motion.div 
            className="hidden md:flex items-center gap-6 ml-8"
            variants={itemVariants}
          >

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link 
                to="/gallery" 
                className="text-dblue hover:text-blue-600 font-medium uppercase text-sm transition-colors relative"
              >
                <span>Gallery</span>
                <motion.div
                  className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600"
                  whileHover={{ width: "100%" }}
                  transition={{ duration: 0.2 }}
                />
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link 
                to="/calendar" 
                className="text-dblue hover:text-blue-600 font-medium uppercase text-sm transition-colors relative"
              >
                <span>Calendar</span>
                <motion.div
                  className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600"
                  whileHover={{ width: "100%" }}
                  transition={{ duration: 0.2 }}
                />
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link 
                to="/contact-us" 
                className="text-dblue hover:text-blue-600 font-medium uppercase text-sm transition-colors relative"
              >
                <span>Contact Us</span>
                <motion.div
                  className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600"
                  whileHover={{ width: "100%" }}
                  transition={{ duration: 0.2 }}
                />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Mobile Dropdown Menu */}
        <motion.div
          className="md:hidden overflow-hidden"
          variants={mobileMenuVariants}
          initial="closed"
          animate={isMobileMenuOpen ? "open" : "closed"}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          style={{ pointerEvents: isMobileMenuOpen ? 'auto' : 'none' }}
        >
          <div className="bg-white border-t border-gray-200 py-4 space-y-2">
            <motion.div
              whileHover={{ x: 5 }}
            >
              <Link 
                to="/home" 
                className="block px-4 py-2 text-dblue hover:bg-gray-100 font-medium uppercase text-sm transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </Link>
            </motion.div>
            <div>
              <motion.button 
                className="w-full text-left px-4 py-2 text-dblue hover:bg-gray-100 font-medium uppercase text-sm transition-all duration-200 rounded-md relative group"
                onClick={() => setIsAboutMobileOpen((prev) => !prev)}
                whileHover={{ x: 5 }}
                whileTap={{ scale: 0.98 }}
              >
                <motion.div
                  className="absolute inset-0 bg-blue-50 opacity-0 group-hover:opacity-100 rounded-md transition-opacity duration-200"
                />
                <span className="relative z-10 flex items-center justify-between">
                About
                  <motion.svg
                    className="w-4 h-4 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    animate={{ rotate: isAboutMobileOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </motion.svg>
                </span>
              </motion.button>
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ 
                  height: isAboutMobileOpen ? "auto" : 0,
                  opacity: isAboutMobileOpen ? 1 : 0
                }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden pl-4"
                style={{ pointerEvents: isAboutMobileOpen ? 'auto' : 'none' }}
              >
                <motion.div
                  initial="hidden"
                  animate={isAboutMobileOpen ? "visible" : "hidden"}
                  variants={{
                    hidden: { opacity: 0 },
                    visible: { 
                      opacity: 1,
                      transition: { 
                        staggerChildren: 0.05,
                        delayChildren: 0.1
                      }
                    }
                  }}
                >
                  <motion.div variants={dropdownItemVariants}>
                    <Link 
                      to="/about/educators" 
                      className="block px-4 py-2 text-dblue hover:bg-blue-50 hover:text-blue-700 text-sm font-medium transition-all duration-200 rounded-md mx-2 relative overflow-hidden group" 
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      />
                      <span className="relative z-10">Educators</span>
                    </Link>
                  </motion.div>
                  <motion.div variants={dropdownItemVariants}>
                    <Link 
                      to="/about/admin-staff" 
                      className="block px-4 py-2 text-dblue hover:bg-blue-50 hover:text-blue-700 text-sm font-medium transition-all duration-200 rounded-md mx-2 relative overflow-hidden group" 
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      />
                      <span className="relative z-10">Admin Staff</span>
                    </Link>
                  </motion.div>
                  <motion.div variants={dropdownItemVariants}>
                    <Link 
                      to="/about/classrooms" 
                      className="block px-4 py-2 text-dblue hover:bg-blue-50 hover:text-blue-700 text-sm font-medium transition-all duration-200 rounded-md mx-2 relative overflow-hidden group" 
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      />
                      <span className="relative z-10">Classrooms</span>
                    </Link>
                  </motion.div>
                  <motion.div variants={dropdownItemVariants}>
                    <Link 
                      to="/about/environment" 
                      className="block px-4 py-2 text-dblue hover:bg-blue-50 hover:text-blue-700 text-sm font-medium transition-all duration-200 rounded-md mx-2 relative overflow-hidden group" 
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      />
                      <span className="relative z-10">Environment</span>
                    </Link>
                  </motion.div>
                  <motion.div variants={dropdownItemVariants}>
                    <Link 
                      to="/about/home-made-meals" 
                      className="block px-4 py-2 text-dblue hover:bg-blue-50 hover:text-blue-700 text-sm font-medium transition-all duration-200 rounded-md mx-2 relative overflow-hidden group" 
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      />
                      <span className="relative z-10">Home-made Meals</span>
                    </Link>
                  </motion.div>
                  <motion.div variants={dropdownItemVariants}>
                    <Link 
                      to="/about/teaching-methods" 
                      className="block px-4 py-2 text-dblue hover:bg-blue-50 hover:text-blue-700 text-sm font-medium transition-all duration-200 rounded-md mx-2 relative overflow-hidden group" 
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-blue-50 to-blue-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      />
                      <span className="relative z-10">Teaching Methods</span>
                    </Link>
                  </motion.div>
                </motion.div>
              </motion.div>
            </div>
            <motion.div
              whileHover={{ x: 5 }}
            >
              <Link 
                to="/application" 
                className="block px-4 py-2 text-dblue hover:bg-gray-100 font-medium uppercase text-sm transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Enrollment
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ x: 5 }}
            >
              <Link 
                to="/our-staff" 
                className="block px-4 py-2 text-dblue hover:bg-gray-100 font-medium uppercase text-sm transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Our Staff
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ x: 5 }}
            >
              <Link 
                to="/gallery" 
                className="block px-4 py-2 text-dblue hover:bg-gray-100 font-medium uppercase text-sm transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Gallery
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ x: 5 }}
            >
              <Link 
                to="/calendar" 
                className="block px-4 py-2 text-dblue hover:bg-gray-100 font-medium uppercase text-sm transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Calendar
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ x: 5 }}
            >
              <Link 
                to="/contact-us" 
                className="block px-4 py-2 text-dblue hover:bg-gray-100 font-medium uppercase text-sm transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact Us
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.nav>
  );
}